/**
 * Challenge Leaderboard — rankings for the current weekly challenge.
 */
import { useEffect, useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  RefreshControl,
  TouchableOpacity,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter } from 'expo-router';
import { ArrowLeft, Flame, Clock, Lock } from 'lucide-react-native';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { TYPO } from '@/constants/typography';
import {
  getCurrentChallenge,
  getChallengeLeaderboard,
  getWeekDates,
  getPastChallenges,
  type WeeklyChallenge,
  type ChallengeLeaderboardEntry,
  type PastChallenge,
} from '@/lib/weeklyChallenges';
import { useSubscription } from '@/hooks/useSubscription';
import { hasAtLeast } from '@/lib/subscription';
import { RADIUS, SPACE } from '@/constants/designTokens';

export default function ChallengeLeaderboardScreen() {
  const router = useRouter();
  const { theme } = useTheme();
  const c = theme.colors;
  const { session } = useAuth();
  const userId = session?.user?.id;
  const { tier } = useSubscription();
  const isPro = hasAtLeast(tier, 'pro');

  const [challenge, setChallenge] = useState<WeeklyChallenge | null>(null);
  const [entries, setEntries] = useState<ChallengeLeaderboardEntry[]>([]);
  const [pastChallenges, setPastChallenges] = useState<PastChallenge[]>([]);
  const [daysLeft, setDaysLeft] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const ch = await getCurrentChallenge();
    setChallenge(ch);
    if (!ch) { setLoading(false); return; }
    const lb = await getChallengeLeaderboard(ch.id);
    setEntries(lb);
    const { end } = getWeekDates();
    setDaysLeft(Math.max(0, Math.ceil((end.getTime() - Date.now()) / (1000 * 60 * 60 * 24))));
    // Load past challenges for Pro users
    try {
      const past = await getPastChallenges(4);
      setPastChallenges(past);
    } catch { /* silent */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const onRefresh = useCallback(async () => {
    setRefreshing(true);
    await load();
    setRefreshing(false);
  }, [load]);

  const currentUser = entries.find(e => e.user_id === userId);

  const formatDate = (d: string) =>
    new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: c.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity onPress={() => router.back()} hitSlop={12}>
          <ArrowLeft size={24} color={c.text} />
        </TouchableOpacity>
        <Flame size={20} color={c.accent} style={{ marginLeft: 12 }} />
        <Text style={[styles.headerTitle, { color: c.text, fontFamily: TYPO.heading.bold }]}>
          CHALLENGE
        </Text>
      </View>

      {challenge && (
        <View style={styles.challengeInfo}>
          <Text style={[styles.challengeTitle, { color: c.text, fontFamily: TYPO.heading.bold }]}>
            {challenge.title}
          </Text>
          <Text style={[styles.challengeDesc, { color: c.textSecondary }]}>
            {challenge.description}
          </Text>
          <View style={styles.countdownRow}>
            <Clock size={14} color={c.accent} />
            <Text style={[styles.countdownText, { color: c.accent, fontFamily: TYPO.body.semibold }]}>
              {daysLeft}d left
            </Text>
          </View>
        </View>
      )}

      <ScrollView
        style={styles.list}
        contentContainerStyle={entries.length === 0 ? styles.emptyContainer : undefined}
        refreshControl={
          <RefreshControl refreshing={refreshing} onRefresh={onRefresh} tintColor={c.accent} />
        }
      >
        {!loading && entries.length === 0 && (
          <View style={styles.emptyState}>
            <Flame size={48} color={c.textTertiary} />
            <Text style={[styles.emptyText, { color: c.textSecondary, fontFamily: TYPO.heading.bold }]}>
              NO SUBMISSIONS YET
            </Text>
            <Text style={[styles.emptySubtext, { color: c.textTertiary }]}>
              Be the first to submit a score!
            </Text>
          </View>
        )}

        {entries.map((e) => {
          const isMe = e.user_id === userId;
          return (
            <View
              key={e.user_id}
              style={[
                styles.row,
                { borderBottomColor: c.border },
                isMe && { backgroundColor: c.accent + '10' },
              ]}
            >
              <Text style={[styles.rank, { color: e.rank <= 3 ? c.accent : c.textSecondary, fontFamily: TYPO.heading.bold }]}>
                #{e.rank}
              </Text>
              <View style={styles.nameCol}>
                <Text style={[styles.displayName, { color: c.text, fontFamily: TYPO.body.semibold }]} numberOfLines={1}>
                  {e.display_name ?? e.handle ?? 'Unknown'}
                </Text>
                {e.handle && (
                  <Text style={[styles.handle, { color: c.textTertiary }]} numberOfLines={1}>
                    @{e.handle}
                  </Text>
                )}
              </View>
              <Text style={[styles.score, { color: c.text, fontFamily: TYPO.heading.bold }]}>
                {Math.round(e.score)}
              </Text>
            </View>
          );
        })}
        {/* Past Challenges — Pro gated */}
        {!loading && (
          <View style={styles.pastSection}>
            <Text style={[styles.pastTitle, { color: c.textSecondary, fontFamily: TYPO.heading.bold }]}>
              PAST CHALLENGES
            </Text>
            {isPro ? (
              pastChallenges.length > 0 ? (
                pastChallenges.map((pc) => (
                  <View key={pc.id} style={[styles.pastCard, { backgroundColor: c.surface, borderColor: c.border }]}>
                    <Text style={[styles.pastName, { color: c.text, fontFamily: TYPO.body.semibold }]}>{pc.title}</Text>
                    <Text style={[styles.pastWinner, { color: c.accent }]}>
                      🏆 {pc.winner_name ?? 'No winner'}
                    </Text>
                  </View>
                ))
              ) : (
                <Text style={{ color: c.textTertiary, fontSize: 12 }}>No past challenges yet</Text>
              )
            ) : (
              <View style={styles.pastLocked}>
                <Lock size={14} color={c.textTertiary} />
                <Text style={[styles.pastLockedText, { color: c.textTertiary }]}>
                  View past challenge results with Pro
                </Text>
              </View>
            )}
          </View>
        )}
      </ScrollView>

      {/* Current user highlight + submit button */}
      <View style={[styles.bottomBar, { backgroundColor: c.surface, borderTopColor: c.border }]}>
        {currentUser && (
          <View style={styles.myRankRow}>
            <Text style={[styles.myRankLabel, { color: c.textSecondary }]}>Your rank</Text>
            <Text style={[styles.myRankValue, { color: c.accent, fontFamily: TYPO.heading.bold }]}>
              #{currentUser.rank} · {Math.round(currentUser.score)}
            </Text>
          </View>
        )}
        <TouchableOpacity
          style={[styles.submitBtn, { backgroundColor: c.accent }]}
          onPress={() => router.back()}
        >
          <Text style={[styles.submitText, { fontFamily: TYPO.heading.bold }]}>
            SUBMIT SCORE
          </Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  headerTitle: {
    fontSize: 18,
    letterSpacing: 1,
    marginLeft: 8,
  },
  challengeInfo: {
    paddingHorizontal: 16,
    paddingBottom: 12,
  },
  challengeTitle: {
    fontSize: 20,
    marginBottom: 4,
  },
  challengeDesc: {
    fontSize: 13,
    lineHeight: 18,
    marginBottom: 8,
  },
  countdownRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  countdownText: { fontSize: 13, letterSpacing: 0.5 },
  list: { flex: 1 },
  emptyContainer: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  emptyState: { alignItems: 'center', gap: 8 },
  emptyText: { fontSize: 16, letterSpacing: 1, marginTop: 8 },
  emptySubtext: { fontSize: 13 },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  rank: { width: 40, fontSize: 15 },
  nameCol: { flex: 1 },
  displayName: { fontSize: 14 },
  handle: { fontSize: 12, marginTop: 1 },
  score: { fontSize: 16, minWidth: 44, textAlign: 'right' },
  bottomBar: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
  },
  myRankRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 10,
  },
  myRankLabel: { fontSize: 13 },
  myRankValue: { fontSize: 14 },
  submitBtn: {
    paddingVertical: 14,
    alignItems: 'center',
    borderRadius: 2,
  },
  submitText: {
    fontSize: 14,
    color: '#000',
    letterSpacing: 1,
  },
  pastSection: { paddingHorizontal: 16, paddingTop: 20, paddingBottom: 16, gap: 8 },
  pastTitle: { fontSize: 11, letterSpacing: 1.5 },
  pastCard: { borderWidth: 1, borderRadius: 2, padding: 12, flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
  pastName: { fontSize: 13, flex: 1 },
  pastWinner: { fontSize: 12 },
  pastLocked: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingVertical: 12 },
  pastLockedText: { fontSize: 12 },
});
